import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { exportReport } from "./exportReport";   // ← separate export file

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const STATUS_OPTIONS  = ["ACTIVE", "ENDED", "SUPERSEDED"];


// ── Icons ─────────────────────────────────────────────────────────────────────
const ChevronDown = ({ size = 10, color }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5L8 3.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ExportIcon = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M4 7l4 4 4-4M2 13h12" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const CheckIcon = ({ color }) => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M2 5.5l2.5 2.5L9 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const SearchIcon = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="5" stroke={color} strokeWidth="1.5"/>
    <path d="M11 11l3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ── useClickOutside ───────────────────────────────────────────────────────────
const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) callback(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, callback]);
};

// ── MultiSelectDropdown ───────────────────────────────────────────────────────
const MultiSelectDropdown = ({
  label, options, selected, onChange,
  getLabel, getValue,
  tokens, isDark,
  searchable = false,
}) => {
  const [open,   setOpen]   = useState(false);
  const [query,  setQuery]  = useState("");
  const ref      = useRef(null);
  const inputRef = useRef(null);
  useClickOutside(ref, () => { setOpen(false); setQuery(""); });

  useEffect(() => {
    if (open && searchable && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open, searchable]);

  const isActive = selected.length > 0;

  const toggle = (val) => {
    const s = String(val);
    onChange(
      selected.map(String).includes(s)
        ? selected.filter((x) => String(x) !== s)
        : [...selected, val]
    );
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((opt) => {
      const lbl = getLabel ? getLabel(opt) : String(opt);
      return lbl != null && String(lbl).toLowerCase().includes(q);
    });
  }, [options, query, getLabel]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 32, padding: "0 10px", borderRadius: 8,
          border: `1px solid ${isActive ? tokens.accent : tokens.border2}`,
          background: isActive
            ? isDark ? "rgba(40,179,180,0.14)" : "rgba(40,179,180,0.08)"
            : tokens.surface,
          cursor: "pointer", userSelect: "none",
          fontSize: 12, fontWeight: 500,
          color: isActive ? tokens.accent : tokens.text2,
          transition: "all 0.15s", whiteSpace: "nowrap",
        }}
      >
        {isActive && (
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: tokens.accent, flexShrink: 0 }} />
        )}
        <span>{label}</span>
        {isActive && (
          <span style={{
            background: tokens.accent, color: "#fff",
            borderRadius: 10, fontSize: 10, fontWeight: 600,
            padding: "1px 5px", lineHeight: 1.5,
          }}>
            {selected.length}
          </span>
        )}
        <ChevronDown color={isActive ? tokens.accent : tokens.text3} size={10} />
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          minWidth: 200, maxWidth: 280,
          background: tokens.surface,
          border: `1px solid ${tokens.border2}`,
          borderRadius: 10,
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.5)"
            : "0 8px 24px rgba(3,81,77,0.12)",
          zIndex: 999, overflow: "hidden",
        }}>
          {searchable && (
            <div style={{
              padding: "8px 10px",
              borderBottom: `1px solid ${tokens.border}`,
              display: "flex", alignItems: "center", gap: 7,
              background: tokens.surface2,
            }}>
              <SearchIcon color={tokens.text3} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label}…`}
                style={{
                  flex: 1, border: "none", outline: "none",
                  background: "transparent",
                  fontSize: 12, color: tokens.text,
                }}
              />
              {query && (
                <span
                  onClick={() => setQuery("")}
                  style={{ cursor: "pointer", color: tokens.text3, fontSize: 14, lineHeight: 1 }}
                >
                  ×
                </span>
              )}
            </div>
          )}

          <div style={{ maxHeight: 220, overflowY: "auto", padding: "4px 0" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: 12, color: tokens.text3 }}>
                No results
              </div>
            ) : (
              filtered.map((opt) => {
                const val     = getValue ? getValue(opt) : opt;
                const lbl     = getLabel ? getLabel(opt) : opt;
                const checked = selected.map(String).includes(String(val));
                return (
                  <div
                    key={String(val)}
                    onClick={() => toggle(val)}
                    onMouseEnter={(e) => {
                      if (!checked) e.currentTarget.style.background =
                        isDark ? "rgba(255,255,255,0.04)" : "rgba(3,81,77,0.04)";
                    }}
                    onMouseLeave={(e) => {
                      if (!checked) e.currentTarget.style.background =
                        checked
                          ? isDark ? "rgba(40,179,180,0.1)" : "rgba(40,179,180,0.06)"
                          : "transparent";
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "7px 12px", cursor: "pointer",
                      fontSize: 12, fontWeight: checked ? 500 : 400,
                      color: checked ? tokens.accent : tokens.text,
                      background: checked
                        ? isDark ? "rgba(40,179,180,0.1)" : "rgba(40,179,180,0.06)"
                        : "transparent",
                      transition: "background 0.1s",
                    }}
                  >
                    <div style={{
                      width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                      border: `1.5px solid ${checked ? tokens.accent : tokens.border3}`,
                      background: checked ? tokens.accent : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s",
                    }}>
                      {checked && <CheckIcon color="#fff" />}
                    </div>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {lbl ?? "—"}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {selected.length > 0 && (
            <div style={{
              padding: "6px 12px",
              borderTop: `1px solid ${tokens.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: tokens.surface2,
            }}>
              <span style={{ fontSize: 11, color: tokens.text3 }}>
                {selected.length} selected
              </span>
              <span
                onClick={() => onChange([])}
                style={{ fontSize: 11, fontWeight: 600, color: tokens.accent, cursor: "pointer" }}
              >
                Clear
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── MonthNavigator ────────────────────────────────────────────────────────────
const MonthNavigator = ({ year, month, navigateMonth, tokens, isDark }) => {
  const arrowStyle = {
    background: "none", border: "none", cursor: "pointer",
    color: tokens.accent, fontSize: 16, lineHeight: 1,
    padding: "0 2px", display: "flex", alignItems: "center",
    justifyContent: "center", borderRadius: 4, transition: "background 0.1s",
  };
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      height: 32, padding: "0 10px", borderRadius: 8,
      border: `1px solid ${tokens.accent}`,
      background: isDark ? "rgba(40,179,180,0.14)" : "rgba(40,179,180,0.08)",
      userSelect: "none",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: tokens.accent, flexShrink: 0 }} />
      <button
        style={arrowStyle}
        onClick={() => navigateMonth(-1)}
        onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(3,81,77,0.08)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
      >‹</button>
      <span style={{ fontSize: 12, fontWeight: 600, color: tokens.accent, minWidth: 52, textAlign: "center" }}>
        {MONTH_NAMES[month - 1]} {year}
      </span>
      <button
        style={arrowStyle}
        onClick={() => navigateMonth(1)}
        onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(3,81,77,0.08)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
      >›</button>
    </div>
  );
};

// ── ReportFilterBar ───────────────────────────────────────────────────────────
const ReportFilterBar = ({
  year, month, navigateMonth,
  macs, associates,
  selectedMacIds,       setSelectedMacIds,
  selectedAssociateIds, setSelectedAssociateIds,
  selectedStatuses,     setSelectedStatuses,
  selectedFteValues,    setSelectedFteValues,
  rows = [],
  onRun, onReset,
  hideMacFilter = false,
  hideExportButton = false,
}) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tokens = theme.custom;

  const dp = { tokens, isDark };

  /* ── Export handler ──────────────────────────────────────────────────────── */
  const handleExport = async () => {
    await exportReport({
      year,
      month,
      rows,
      filters: {
        macIds:       selectedMacIds,
        associateIds: selectedAssociateIds,
        statuses:     selectedStatuses,
        fteValues:    selectedFteValues,
      },
      macs,
      associates,
    });
  };

  return (
    <div style={{
      position: "sticky",
      top: 0,
      zIndex: 100,
      background: tokens.surface,
      borderBottom: `1px solid ${tokens.border}`,
    }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "10px 16px",
      background: tokens.surface,
      borderRadius: 12,
      border: `1px solid ${tokens.border}`,
      flexWrap: "wrap",
    }}>
      <span style={{
        fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
        textTransform: "uppercase", color: tokens.text3,
        marginRight: 2, flexShrink: 0,
      }}>
        Filters
      </span>

      <MonthNavigator year={year} month={month} navigateMonth={navigateMonth} {...dp} />

      {!hideMacFilter && (
        <MultiSelectDropdown
          label="MAC"
          options={macs}
          selected={selectedMacIds}
          onChange={setSelectedMacIds}
          getValue={(m) => m.macId}
          getLabel={(m) => m.macName}
          searchable
          {...dp}
        />
      )}

      

      {/* <MultiSelectDropdown
        label="Status"
        options={STATUS_OPTIONS}
        selected={selectedStatuses}
        onChange={setSelectedStatuses}
        {...dp}
      /> */}

      

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 1, height: 20, background: tokens.border2, flexShrink: 0, margin: "0 4px" }} />

        <button
          onClick={onReset}
          onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "rgba(3,81,77,0.05)"; e.currentTarget.style.color = tokens.text; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = tokens.text2; }}
          style={{
            height: 32, padding: "0 14px", borderRadius: 8,
            border: `1px solid ${tokens.border2}`,
            background: "transparent", color: tokens.text2,
            fontSize: 12, fontWeight: 500, cursor: "pointer", transition: "all 0.15s",
          }}
        >
          Reset
        </button>

        {/* ── Export button — calls handleExport ────────────────────────── */}
        {!hideExportButton && (
  <button
    onClick={handleExport}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = isDark
        ? "rgba(255,255,255,0.05)"
        : "rgba(3,81,77,0.05)";
      e.currentTarget.style.color = tokens.text;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.color = tokens.text2;
    }}
    style={{
      height: 32,
      padding: "0 14px",
      borderRadius: 8,
      border: `1px solid ${tokens.border2}`,
      background: "transparent",
      color: tokens.text2,
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.15s",
    }}
  >
    <ExportIcon color={tokens.text3} />
    Export
  </button>
)}
      </div>
    </div>
    </div>
  );
};

export default ReportFilterBar;